import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Button } from '../../../../../iba-components/Button'
import { Input } from '../../../../../iba-components/Input'
import { Select } from '../../../../../iba-components/Select' // 追加
import { Modal } from '../../../../../iba-components/Modal'
import { Table } from '../../../../../iba-components/Table'
import { showPopBanner } from '../../../../../iba-components/PopBanner'
import { getColumnList } from './tableColumns'
import { PWC_DOMAIN } from '../../../../constants'
import './userManagement.css'
import {
    insertUser,
    updateUser,
    getALLUserList,
    getUser,
    deleteUser,
    activeUser
} from '../../../../api/client_managementV2'
import ConfirmModal from '../../../../components/ConfirmModal'
import { Loading } from '../../../../components/Loading'
import LoadingMask from '../../../../components/LoadingMask'
import { eMailReg } from '../../../../api/common'
import styles from './userManagement.module.css'

const MAX_LENGTH = { min: 30, mid_one: 50, mid_two: 60, max: 100 }

const initUserInfo = {
    id: '',
    clientid: '',
    firstname: '',
    lastname: '',
    mailaddress: '',
    organization: '',
    title: '',
    analysispermission: 0 // デフォルトは0（なし）
}

const Footer = ({ t, setVisibleModal, submitUser, action, disabled = false }) => {
    return (
        <div className="button-comp">
            <Button
                disabled={disabled}
                width="68px"
                label={t('clientManagement.cancel')}
                kind="text"
                isGray
                onClick={() => setVisibleModal(false)}
            />
            <Button
                disabled={disabled}
                width="68px"
                label={
                    action === 'update'
                        ? t('clientManagement.update')
                        : action === 'add'
                        ? t('clientManagement.add')
                        : t('clientManagement.userManagement.reactive')
                }
                kind="text"
                onClick={submitUser}
            />
        </div>
    )
}

// ヘルパー関数
const getValueOfSpecifiedKey = (dataList, findKey, findValue, specifiedKey) => {
    const findObject = dataList.find((data) => {
        return data[findKey] === findValue
    })
    return findObject ? findObject[specifiedKey] : ''
}

const UserManagement = ({ id }) => {
    const { t } = useTranslation()

    const user = useSelector((state) => state.userInfo)
    const [visibleModal, setVisibleModal] = useState(false)
    const [selection, setSelection] = useState([])

    // table
    const [isUserLoading, setIsUserLoading] = useState(false)
    const [isUserIdMissing, setIsUserIdMissing] = useState(true)
    const [list, setList] = useState([])
    const [isTableLoading, setIsTableLoading] = useState(false)
    const [disabledCheckboxList, setDisabledCheckboxList] = useState([])

    // set user info
    const [userInfo, setUserInfo] = useState(initUserInfo)
    const [originalUserMail, setOriginalUserMail] = useState('')

    // touched and submitted
    const [touchedList, setTouchedList] = useState({})
    const [submitted, setSubmitted] = useState(false)

    // update or add user
    const [action, setAction] = useState()

    // mail error
    const [mailError, setMailError] = useState({
        mailaddress_error: false
    })

    const [isLoading, setIsLoading] = useState(false)

    // delete user
    const [showDeleteUserModal, setShowDeleteUserModal] = useState(false)

    // active user
    const [showActiveUserModal, setShowActiveUserModal] = useState(false)

    // analysispermission select options
    const analysisPermissionSelectList = [
        {
            content: t('clientManagement.userManagement.userInfo.analysispermission.none') || 'なし',
            code: 0
        },
        {
            content: t('clientManagement.userManagement.userInfo.analysispermission.available') || 'あり',
            code: 2
        }
    ]

    // user info list
    const userInfoArr = [
        {
            label: `${t(`clientManagement.userManagement.userInfo.userLName`)}`,
            name: 'lastname',
            value: userInfo.lastname,
            maxLength: MAX_LENGTH.min,
            required: true,
            hasError: true
        },
        {
            label: `${t(`clientManagement.userManagement.userInfo.userFName`)}`,
            name: 'firstname',
            value: userInfo.firstname,
            maxLength: MAX_LENGTH.min,
            required: true,
            hasError: true
        },
        {
            label: t(`clientManagement.userManagement.userInfo.mailAddress`),
            name: 'mailaddress',
            value: userInfo.mailaddress,
            maxLength: MAX_LENGTH.max,
            inputLimited: true,
            mailChecked: true,
            required: true,
            hasError: true
        },
        {
            label: t(`clientManagement.userManagement.userInfo.organization`),
            name: 'organization',
            value: userInfo.organization,
            maxLength: MAX_LENGTH.mid_two
        },
        {
            label: t(`clientManagement.userManagement.userInfo.title`),
            name: 'title',
            value: userInfo.title,
            maxLength: MAX_LENGTH.mid_two
        },
        // 現在の権限がFullかどうかで表示を分岐
        userInfo.analysispermission === 1
            ? {
                  // Fullの場合：読み取り専用で表示
                  label: t(`clientManagement.userManagement.userInfo.analysispermission.label`),
                  name: 'analysispermission',
                  value: t('clientManagement.userManagement.userInfo.analysispermission.full'),
                  isSelect: true,
                  isReadOnly: true // 読み取り専用フラグ
              }
            : {
                  // None/Availableの場合：セレクト表示
                  label: t(`clientManagement.userManagement.userInfo.analysispermission.label`),
                  name: 'analysispermission',
                  value: getValueOfSpecifiedKey(
                      analysisPermissionSelectList,
                      'code',
                      userInfo.analysispermission,
                      'content'
                  ),
                  isSelect: true,
                  selectList: analysisPermissionSelectList,
                  isRequired: false
              }
    ]

    // fetch table
    const fetchData = async () => {
        setIsTableLoading(true)
        setSelection([])
        const { userlist } = await getALLUserList({
            clientid: id
        })
        setList(userlist)
        const deleteUserList = userlist.filter(({ delflg }) => delflg === 1).map(({ id }) => id)
        setDisabledCheckboxList(deleteUserList)
        setIsTableLoading(false)
    }

    // piamuseridでpiamにgetUserしてメールアドレスを取得する
    const fetchSelectUser = async (original) => {
        setIsUserLoading(true)
        let mailaddress = ''
        // piamuseridがないのは認証前ユーザー。
        if (original.piamuserid) {
            const { user } = await getUser({
                piamUserId: original.piamuserid
            })
            mailaddress = user.email
            setIsUserIdMissing(false)
        } else {
            setIsUserIdMissing(true)
        }
        setUserInfo({ ...original, mailaddress })
        setOriginalUserMail(mailaddress)
        setIsUserLoading(false)
    }

    useEffect(() => {
        fetchData()
    }, [])

    const handleClickSelectBox = (_clickData, e) => {
        e.stopPropagation()
    }

    const handleClickRow = (original) => {
        console.log('handleClickRow1')
        const delFlg = original.delflg
        if (delFlg === 1) {
            setAction('active')
        } else {
            setAction('update')
        }
        setSubmitted(false)
        setTouchedList({})
        fetchSelectUser(original)
        setVisibleModal(true)
    }

    const onChangeSelectedData = (selectedData) => {
        setSelection(selectedData)
    }

    // add
    const doAdd = () => {
        setSubmitted(false)
        setTouchedList({})
        setUserInfo(initUserInfo)
        setAction('add')
        setVisibleModal(true)
    }

    // delete user
    const deleteUserList = async () => {
        setIsLoading(true)

        await deleteUser({
            userids: selection.join(),
            createdby: user.userinfo.username
        }).finally(() => {
            setIsLoading(false)
        })

        setShowDeleteUserModal(false)

        fetchData()
        showPopBanner({
            message: t(`signUpClient.warningMsg.registrationSuccess`),
            status: 'success',
            duration: 2000
        })
    }

    // change user info
    const onChangeInput = (val, ele) => {
        if (ele.inputLimited) {
            setUserInfo({
                ...userInfo,
                [ele.name]: val.replace(
                    /[^\a-\z\A-\Z0-9\!\@\#\$\%\^\&\*\(\)\-\_\=\+\[\]\{\}\\\|\;\:\'\"\,\<\.\>\/\?\`\~\ ]/g, // eslint-disable-line
                    ''
                )
            })
        } else {
            setUserInfo({
                ...userInfo,
                [ele.name]: val
            })
        }

        // check email
        if (val.trim() && ele.mailChecked) {
            const pattern = eMailReg

            !pattern.test(val.trim())
                ? setMailError({ ...mailError, [`${ele.name}_error`]: true })
                : setMailError({ ...mailError, [`${ele.name}_error`]: false })
        }
    }

    // update/add user
    const submitUser = async () => {
        if (action === 'active') {
            setShowActiveUserModal(true)
            return
        }

        setSubmitted(true)

        const isUpdate = action === 'update'
        const payload = {
            roleid: 0,
            clientid: id,
            lastname: userInfo.lastname,
            firstname: userInfo.firstname,
            mailaddress: userInfo.mailaddress,
            organization: userInfo.organization,
            title: userInfo.title,
            analysispermission: userInfo.analysispermission,
            createdby: user.userinfo.username
        }

        if (isUpdate) {
            payload.userid = userInfo.id
        }

        const isNotEmpty = ['firstname', 'lastname', 'mailaddress'].every((k) => payload[k])

        if (isNotEmpty && !mailError.mailaddress_error) {
            setIsLoading(true)
            const res = isUpdate
                ? await updateUser(payload).catch((_e) => undefined)
                : await insertUser(payload).catch((_e) => undefined)
            setIsLoading(false)

            let message = ''
            if (!res) {
                message = 'Internal Server Error'
            } else if (res?.status === 'DUPLICATE_EMAIL_ADDRESS') {
                message = t(`signUpClient.warningMsg.duplicatedEmail`)
            } else if (res?.hasError) {
                message = res?.desc || t(`signUpClient.warningMsg.registrationError`)
            }

            if (message) {
                showPopBanner({
                    message,
                    status: 'error',
                    duration: 5000
                })
                return
            }

            setVisibleModal(false)
            fetchData()

            showPopBanner({
                message: t(`signUpClient.warningMsg.registrationSuccess`),
                status: 'success',
                duration: 5000
            })
        }
    }

    const reactiveUser = async () => {
        setIsLoading(true)

        await activeUser({
            userid: userInfo.id,
            createdby: user.userinfo.username
        }).finally(() => {
            setIsLoading(false)
        })

        setShowActiveUserModal(false)
        setVisibleModal(false)

        // reload table
        fetchData()
        showPopBanner({
            message: t(`signUpClient.warningMsg.registrationSuccess`),
            status: 'success',
            duration: 2000
        })
    }

    // FIXME: 条件を綺麗に整理したい
    const checkErrorInput = (ele) => {
        return (
            ele.hasError &&
            (((!ele.value.trim() || (ele.mailChecked && mailError[`${ele.name}_error`])) &&
                (touchedList[`${ele.name}_touched`] || submitted)) ||
                (!ele.value.trim() && (touchedList[`${ele.name}_touched`] || submitted)))
        )
    }

    const getErrorMsg = (ele) => {
        const isEmpty = !ele.value?.trim()
        const isMail = ele.name === 'mailaddress'

        if (isEmpty) return `${ele.label}${t(`signUpClient.warningMsg.isRequired`)}`

        return isMail ? t(`signUpClient.warningMsg.incorrectEmailFormat`) : ''
    }

    // Selectコンポーネント用の関数
    const getUserInfoSelect = (ele) => {
        const isDisabledInput = (name) => {
            if (action === 'add') return false
            if (isUserIdMissing) return true
            if (action === 'active') return true
            return name === 'mailaddress' ? PWC_DOMAIN.test(originalUserMail) : false
        }

        return (
            <div key={ele.name} className="user-info-select-container">
                <Select
                    itemKey="code"
                    width={560}
                    dataList={ele.selectList}
                    handleChangeSelect={(value) => {
                        setUserInfo({
                            ...userInfo,
                            [ele.name]: value
                        })
                    }}
                    inputProps={{
                        label: ele.label,
                        required: ele.isRequired,
                        placeholder: 'Please select',
                        value: ele.value,
                        disabled: isDisabledInput(ele.name) || ele.isReadOnly,
                        onBlur: () => {
                            setTouchedList({
                                ...touchedList,
                                [`${ele.name}_touched`]: true
                            })
                        }
                    }}
                    selectedValue={userInfo[ele.name]}
                />
            </div>
        )
    }

    // user info modal
    const userDetailsModal = () => {
        const isDisabledInput = (name) => {
            if (action === 'add') return false
            if (isUserIdMissing) return true
            if (action === 'active') return true
            return name === 'mailaddress' ? PWC_DOMAIN.test(originalUserMail) : false
        }

        return (
            <Modal
                wrapperClassName="user-management-modal"
                placement="top"
                visible={visibleModal}
                width={600}
                onCancel={() => setVisibleModal(false)}
                title={
                    action === 'add'
                        ? t('clientManagement.userManagement.create')
                        : t('clientManagement.userManagement.update')
                }
                footer={
                    <Footer
                        t={t}
                        setVisibleModal={setVisibleModal}
                        submitUser={submitUser}
                        action={action}
                        disabled={action === 'update' && isUserIdMissing}
                    />
                }
            >
                {isUserLoading ? (
                    <>
                        <div className="edit-form-container"></div>
                        <LoadingMask />
                    </>
                ) : (
                    <div className="edit-form-container">
                        {userInfoArr.map((ele) =>
                            ele.isSelect ? (
                                getUserInfoSelect(ele)
                            ) : (
                                <Input
                                    className={styles.input}
                                    key={ele.name}
                                    label={ele.label}
                                    name={ele.name}
                                    required={ele.required}
                                    width={560}
                                    value={ele.value}
                                    disabled={isDisabledInput(ele.name)}
                                    maxLength={ele.maxLength}
                                    onChange={(value) => onChangeInput(value, ele)}
                                    onBlur={(e) => {
                                        setTouchedList({
                                            ...touchedList,
                                            [`${e.target.name}_touched`]: true
                                        })
                                    }}
                                    errorMsg={checkErrorInput(ele) ? getErrorMsg(ele) : ''}
                                />
                            )
                        )}
                        {isUserIdMissing && action === 'update' && (
                            <div className="error">{t('clientManagement.userManagement.warningMsg.userIdMissing')}</div>
                        )}
                    </div>
                )}
                {action !== 'active' && isLoading && <LoadingMask />}
            </Modal>
        )
    }

    return (
        <div className="user-management-container rc-select-table">
            <div className="title-container d-flex justify-content-between align-items-center">
                <Button
                    width="88px"
                    label={t('clientManagement.delete')}
                    onClick={() => setShowDeleteUserModal(true)}
                    disabled={isTableLoading || !selection.length}
                />
                <Button
                    className={styles.add_button}
                    width="88px"
                    label={t('clientManagement.add')}
                    onClick={doAdd}
                    disabled={isTableLoading}
                />
            </div>
            <Loading visible={isTableLoading}>
                <Table
                    columns={getColumnList(t)}
                    data={list}
                    noDataText={isTableLoading ? '' : t('common.noData')}
                    sort={[
                        {
                            id: 'createddate',
                            desc: true
                        }
                    ]}
                    selectType={'checkbox'}
                    selectedId={'id'}
                    isAllCheck
                    isBottomPagination={false}
                    isDownload={false}
                    changeSelectedDataType={'selectBox'}
                    handleClickSelectBox={handleClickSelectBox}
                    handleClickRow={handleClickRow}
                    onChangeSelectedData={onChangeSelectedData}
                    disabledCheckboxList={disabledCheckboxList}
                />
            </Loading>

            {visibleModal && userDetailsModal()}

            {showDeleteUserModal && (
                <ConfirmModal
                    visible={showDeleteUserModal}
                    content={t('clientManagement.userManagement.warningMsg.deleteInfo')}
                    onSubmit={deleteUserList}
                    onCancel={() => setShowDeleteUserModal(false)}
                    isLoading={isLoading}
                />
            )}

            {showActiveUserModal && (
                <ConfirmModal
                    visible={showActiveUserModal}
                    content={t('clientManagement.userManagement.warningMsg.activeInfo')}
                    onSubmit={reactiveUser}
                    onCancel={() => setShowActiveUserModal(false)}
                    isLoading={isLoading}
                />
            )}
        </div>
    )
}

export default UserManagement
